'use client';

import React, { useEffect } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';

import { isRemoteEnvironment } from '../../lib/environment';
import useEnvironment from '../../providers/Environment';
import { scrubUrl } from './scrub_url';

function DatadogSnippet() {
  const config = useEnvironment();

  useEffect(() => {
    if (!config || !isRemoteEnvironment(config)) return;
    datadogRum.init({
      applicationId: config.datadog.appId!,
      clientToken: config.datadog.clientToken!,
      site: 'us3.datadoghq.com',
      service: config.datadog.service,
      env: config.natoma.environment,
      // Specify a version number to identify the deployed version of your application in Datadog
      version: config.datadog.version, // should be short commit hash
      sessionSampleRate: 100,
      sessionReplaySampleRate: 20,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
      enablePrivacyForActionName: true,
      // Strip OAuth `code`, `token`, `state`, etc. from every URL we report
      // to Datadog. The Datadog RumEvent types mark these properties as
      // `readonly`, but the official `beforeSend` API documents them as
      // mutable at runtime — see
      // https://docs.datadoghq.com/real_user_monitoring/guide/enrich-and-control-rum-data/
      // We narrow on `event.type` so we touch the correct shape per variant.
      beforeSend: (event) => {
        // `view` is present on every event variant via CommonProperties.
        const viewBearing = event as {
          view?: { url?: string; referrer?: string };
        };
        if (viewBearing.view?.url) {
          viewBearing.view.url =
            scrubUrl(viewBearing.view.url) ?? viewBearing.view.url;
        }
        if (viewBearing.view?.referrer) {
          viewBearing.view.referrer = scrubUrl(viewBearing.view.referrer);
        }

        if (event.type === 'resource') {
          const resourceBearing = event as { resource?: { url?: string } };
          if (resourceBearing.resource?.url) {
            resourceBearing.resource.url =
              scrubUrl(resourceBearing.resource.url) ??
              resourceBearing.resource.url;
          }
        } else if (event.type === 'action') {
          // Link / button clicks can have URL-shaped names; scrub defensively.
          const actionBearing = event as {
            action?: { target?: { name?: string } };
          };
          const name = actionBearing.action?.target?.name;
          if (name && actionBearing.action?.target) {
            actionBearing.action.target.name = scrubUrl(name) ?? name;
          }
        }

        return true;
      }
    });
    datadogLogs.init({
      clientToken: config.datadog.clientToken!,
      site: 'us3.datadoghq.com',
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
      version: config.datadog.version,
      service: config.datadog.service,
      env: config.natoma.environment,
      // Mirror the RUM scrubbing for forwarded console errors and network
      // logs so OAuth secrets never leave the browser in plaintext.
      beforeSend: (event) => {
        if (event.view?.url) {
          event.view.url = scrubUrl(event.view.url) ?? event.view.url;
        }
        if (event.view?.referrer) {
          event.view.referrer = scrubUrl(event.view.referrer);
        }
        if (event.http?.url) {
          event.http.url = scrubUrl(event.http.url) ?? event.http.url;
        }
        return true;
      }
    });

    return () => {};
  }, [config]);

  return <React.Fragment />;
}

export default DatadogSnippet;
